"use client"

import { Suspense, useEffect } from "react"
import Link from "next/link"
import { useSearchParams } from "next/navigation"
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Loader2,
  Mail,
  User,
} from "lucide-react"

import { AuthFooter } from "@/components/layout"
import { DzpLogo } from "@/components/layout/DzpLogo"
import { DatePicker, Input, Select } from "@/components/ui"

import { SignupProvider, useSignupContext } from "./context"
import { useSignupController } from "./controller"
import { styles } from "./styles"
import type { Gender } from "./types"

const GENDER_OPTIONS = [
  { value: "male", label: "Masculino" },
  { value: "female", label: "Feminino" },
  { value: "non_binary", label: "Não-binário" },
  { value: "prefer_not_to_say", label: "Prefiro não informar" },
]

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  )
}

interface StepIndicatorProps {
  currentStep: "email" | "profile"
}

function StepIndicator({ currentStep }: StepIndicatorProps) {
  return (
    <div className={styles.stepIndicator}>
      <div
        className={
          currentStep === "email" ? styles.stepDotActive : styles.stepDot
        }
      />
      <div
        className={
          currentStep === "profile" ? styles.stepDotActive : styles.stepDot
        }
      />
    </div>
  )
}

function EmailStep() {
  const { email, setEmail, isGoogleLoading, isLoading, error } =
    useSignupContext()
  const { handleGoogleSignup, handleEmailSubmit } = useSignupController()

  return (
    <>
      {error && (
        <div className={styles.errorBox}>
          <p className={styles.errorText}>{error}</p>
        </div>
      )}

      <button
        onClick={handleGoogleSignup}
        disabled={isGoogleLoading}
        className={styles.googleButton}
        aria-label={
          isGoogleLoading ? "Cadastrando com Google..." : "Cadastrar com Google"
        }
      >
        {isGoogleLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <GoogleIcon />
        )}
        Cadastrar com Google
      </button>

      <div className={styles.divider}>
        <div className={styles.dividerLine} />
        <span className={styles.dividerText}>ou</span>
        <div className={styles.dividerLine} />
      </div>

      <form onSubmit={handleEmailSubmit}>
        <div className="mb-4">
          <label className={styles.label}>Email</label>
          <Input
            type="email"
            icon={Mail}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seu@email.com"
            name="email"
            autoComplete="email"
            required
          />
        </div>
        <button
          type="submit"
          disabled={!email || isLoading}
          className={styles.submitButton}
        >
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              Continuar
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </form>

      <p className={styles.footer}>
        Já tem uma conta?{" "}
        <Link href="/login" className={styles.footerLink}>
          Fazer login
        </Link>
      </p>
    </>
  )
}

function ProfileStep() {
  const {
    email,
    firstName,
    lastName,
    birthDate,
    gender,
    setFirstName,
    setLastName,
    setBirthDate,
    setGender,
    setStep,
    isLoading,
    error,
  } = useSignupContext()
  const { handleProfileSubmit } = useSignupController()

  const isFormValid = firstName && lastName && birthDate && gender

  return (
    <>
      <button onClick={() => setStep("email")} className={styles.backButton}>
        <ArrowLeft size={16} />
        Voltar
      </button>

      <p className="text-body-sm mb-6 text-text-500">
        Cadastrando com{" "}
        <span className="font-medium text-text-900">{email}</span>
      </p>

      {error && (
        <div className={styles.errorBox}>
          <p className={styles.errorText}>{error}</p>
        </div>
      )}

      <form onSubmit={handleProfileSubmit}>
        <div className={styles.formGrid}>
          <div>
            <label className={styles.label}>Nome</label>
            <Input
              type="text"
              icon={User}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="João"
              name="firstName"
              autoComplete="given-name"
              required
            />
          </div>
          <div>
            <label className={styles.label}>Sobrenome</label>
            <Input
              type="text"
              icon={User}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Silva"
              name="lastName"
              autoComplete="family-name"
              required
            />
          </div>
        </div>

        <div className={`${styles.formGrid} mb-6`}>
          <div>
            <label className={styles.label}>Data de nascimento</label>
            <DatePicker
              value={birthDate}
              onChange={setBirthDate}
              placeholder="Selecione"
              name="birthDate"
              autocomplete="bday"
            />
          </div>
          <div>
            <label className={styles.label}>Gênero</label>
            <Select
              value={gender}
              onChange={(value) => setGender(value as Gender)}
              options={GENDER_OPTIONS}
              placeholder="Selecione"
              name="gender"
              autocomplete="sex"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading || !isFormValid}
          className={styles.submitButton}
        >
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              Criar conta
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </form>

      <p className={styles.terms}>
        Ao criar uma conta, você concorda com nossos{" "}
        <Link href="/termos" className={styles.termsLink}>
          Termos de Uso
        </Link>{" "}
        e{" "}
        <Link href="/privacidade" className={styles.termsLink}>
          Política de Privacidade
        </Link>
      </p>
    </>
  )
}

function SuccessStep() {
  const { email, setIsMagicLinkSent, setStep } = useSignupContext()

  const handleBack = () => {
    setIsMagicLinkSent(false)
    setStep("email")
  }

  return (
    <div className={styles.successContainer}>
      <div className={styles.successIcon}>
        <CheckCircle className="h-8 w-8 text-success" />
      </div>
      <h2 className={styles.successTitle}>Verifique seu email</h2>
      <p className={styles.successText}>
        Enviamos um link de confirmação para
        <br />
        <span className={styles.successEmail}>{email}</span>
      </p>
      <p className={styles.successHint}>
        Não encontrou? Verifique também a caixa de spam ou lixo eletrônico.
      </p>
      <button onClick={handleBack} className={styles.changeEmailButton}>
        Usar outro email
      </button>
    </div>
  )
}

function SignupForm() {
  const { step, isMagicLinkSent } = useSignupContext()

  if (isMagicLinkSent) {
    return <SuccessStep />
  }

  return (
    <>
      <StepIndicator currentStep={step} />
      {step === "email" ? <EmailStep /> : <ProfileStep />}
    </>
  )
}

export function SignupView() {
  return (
    <div className="flex min-h-screen flex-col bg-gray-50 px-4 py-8">
      <div className="flex flex-1 items-center justify-center">
        <div className={styles.wrapper}>
          <div className={styles.logoSection}>
            <DzpLogo />
            <h1 className={styles.title}>Crie sua conta grátis</h1>
            <p className={styles.subtitle}>
              Comece a aprender um novo idioma hoje
            </p>
          </div>

          <div className={styles.card}>
            <Suspense fallback={null}>
              <CourseSlugPersist />
            </Suspense>
            <SignupProvider>
              <SignupForm />
            </SignupProvider>
          </div>

          <p className={styles.backLink}>
            <Link href="/" className={styles.backLinkText}>
              ← Voltar para o início
            </Link>
          </p>
        </div>
      </div>

      <AuthFooter />
    </div>
  )
}

function CourseSlugPersist() {
  const searchParams = useSearchParams()
  const curso = searchParams.get("curso")

  useEffect(() => {
    if (curso) {
      sessionStorage.setItem("dzp_pending_course", curso)
    }
  }, [curso])

  return null
}
